import React from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type HeroImageV1,
  type RestLCOPopUp,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  CloseOfferButton,
  TitleTheme,
} from '@bbkFunnelComponents/modals/sections';

import './modal.scss';

type Props = {
  modalInfo: RestLCOPopUp;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const RenderLCOPopup: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
  },
  detailedAction,
  close,
}) => {
  const theme = useTheme() as EmotionTheme;

  const imgUrl = (hero as HeroImageV1).url;

  const copy = <CustomerHtml value={details} />;

  return (
    <div
      css={css`
        padding: 0;
      `}
      className={cs('lco-popup', 'display-grid', 'desktop', 'inner-spacer')}
      style={{
        borderRadius: theme.borderRadius,
        overflow: 'hidden',
      }}
    >
      <div style={{ maxHeight: '200px' }}>
        <img
          data-part="image"
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            max-height: 200px;
          `}
        />
        <CloseOfferButton
          newVariant
          onClick={close}
          className={cs('close-btn', 'z-10')}
        />
      </div>
      <CopyContent
        className={cs('align-self--center popup-text')}
        css={{ overflowY: 'scroll', padding: '1rem', maxHeight: '15rem' }}
      >
        <span>
          <TitleTheme
            data-part="title"
            title={titleP}
            css={{ marginBottom: '1rem' }}
          />
        </span>
        <div data-part="description">{copy}</div>
      </CopyContent>
      <div css={{ padding: '1rem' }}>{detailedAction}</div>
    </div>
  );
};

export const LCOPopup = Object.assign(RenderLCOPopup, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'hero', 'url'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 300x200px; < 250kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
